import { cn } from '@skupreme/shared/ui';
import type { IDropdownOption } from 'types/shipment/types';

export const dropdownOptionsIncludeFlag = (arr: IDropdownOption[] = []) =>
  arr.map(opt => {
    let flagSource = flagImages.WW;
    if (opt.name && flagImages[opt.name.toUpperCase()]) {
      flagSource = flagImages[opt.name.toUpperCase()];
    } else if (
      opt.value &&
      typeof opt.value === 'string' &&
      flagImages[opt.value.toUpperCase()]
    ) {
      flagSource = flagImages[opt.value.toUpperCase()];
    } else if (opt.flag && flagImages[opt.flag.toUpperCase()]) {
      flagSource = flagImages[opt.flag.toUpperCase()];
    }

    return {
      ...opt,
      image: { src: flagSource || flagImages.WW },
      flag: null,
    };
  });

interface IFlagProps {
  width?: number;
  height?: number;
  /** A country code such as 'US', or a Semantic flag name. */
  name?: string;
  title?: string;
  className?: string;
}

/**
 * A country flag: our SVG when there is one, else Semantic's CSS flag
 * (`<i class="… flag">`, drawn by the global Semantic stylesheet), as before.
 */
export function Flag({
  width = 20,
  height = 15,
  name,
  title,
  className = '',
}: IFlagProps) {
  const src = name ? flagImages[name.toUpperCase()] : undefined;

  if (!src) {
    return <i className={cn(name?.toLowerCase(), 'flag', className)} />;
  }

  return (
    <img
      src={src}
      title={title}
      alt={title ?? name}
      className={cn('sk:mr-[7px] sk:inline sk:align-middle', className)}
      style={{
        minWidth: width,
        maxWidth: width,
        minHeight: height,
        maxHeight: height,
      }}
    />
  );
}

export const flagImages = {
  AD: '/images/flags/AD.svg',
  AE: '/images/flags/AE.svg',
  AF: '/images/flags/AF.svg',
  AG: '/images/flags/AG.svg',
  AI: '/images/flags/AI.svg',
  AM: '/images/flags/AM.svg',
  AL: '/images/flags/AL.svg',
  AN: '/images/flags/AN.svg',
  AU: '/images/flags/AU.svg',
  AO: '/images/flags/AO.svg',
  AR: '/images/flags/AR.svg',
  AT: '/images/flags/AT.svg',
  AW: '/images/flags/AW.svg',
  AX: '/images/flags/AX.svg',
  AZ: '/images/flags/AZ.svg',
  BA: '/images/flags/BA.svg',
  BB: '/images/flags/BB.svg',
  BD: '/images/flags/BD.svg',
  BE: '/images/flags/BE.svg',
  BF: '/images/flags/BF.svg',
  BG: '/images/flags/BG.svg',
  BH: '/images/flags/BH.svg',
  BI: '/images/flags/BI.svg',
  BJ: '/images/flags/BJ.svg',
  BM: '/images/flags/BM.svg',
  BN: '/images/flags/BN.svg',
  BO: '/images/flags/BO.svg',
  BR: '/images/flags/BR.svg',
  BS: '/images/flags/BS.svg',
  BT: '/images/flags/BT.svg',
  BW: '/images/flags/BW.svg',
  BY: '/images/flags/BY.svg',
  BZ: '/images/flags/BZ.svg',
  CAF: '/images/flags/CAF.svg',
  CA: '/images/flags/CA.svg',
  CD: '/images/flags/CD.svg',
  CF: '/images/flags/CF.svg',
  CG: '/images/flags/CG.svg',
  CH: '/images/flags/CH.svg',
  CI: '/images/flags/CI.svg',
  CL: '/images/flags/CL.svg',
  CM: '/images/flags/CM.svg',
  CN: '/images/flags/CN.svg',
  CO: '/images/flags/CO.svg',
  COC: '/images/flags/COC.svg',
  CR: '/images/flags/CR.svg',
  CSA: '/images/flags/CSA.svg',
  CU: '/images/flags/CU.svg',
  CV: '/images/flags/CV.svg',
  CY: '/images/flags/CY.svg',
  CZ: '/images/flags/CZ.svg',
  DE: '/images/flags/DE.svg',
  DJ: '/images/flags/DJ.svg',
  DK: '/images/flags/DK.svg',
  DM: '/images/flags/DM.svg',
  DO: '/images/flags/DO.svg',
  DZ: '/images/flags/DZ.svg',
  EC: '/images/flags/EC.svg',
  EE: '/images/flags/EE.svg',
  EG: '/images/flags/EG.svg',
  ER: '/images/flags/ER.svg',
  ES: '/images/flags/ES.svg',
  ET: '/images/flags/ET.svg',
  EU: '/images/flags/EU.svg',
  FI: '/images/flags/FI.svg',
  FJ: '/images/flags/FJ.svg',
  FK: '/images/flags/FK.svg',
  FM: '/images/flags/FM.svg',
  FR: '/images/flags/FR.svg',
  GA: '/images/flags/GA.svg',
  GB: '/images/flags/GB.svg',
  GD: '/images/flags/GD.svg',
  GE: '/images/flags/GE.svg',
  GG: '/images/flags/GG.svg',
  GH: '/images/flags/GH.svg',
  GI: '/images/flags/GI.svg',
  GM: '/images/flags/GM.svg',
  GN: '/images/flags/GN.svg',
  GP: '/images/flags/GP.svg',
  GQ: '/images/flags/GQ.svg',
  GR: '/images/flags/GR.svg',
  GT: '/images/flags/GT.svg',
  GW: '/images/flags/GW.svg',
  GY: '/images/flags/GY.svg',
  HK: '/images/flags/HK.svg',
  HN: '/images/flags/HN.svg',
  HR: '/images/flags/HR.svg',
  HT: '/images/flags/HT.svg',
  HU: '/images/flags/HU.svg',
  ID: '/images/flags/ID.svg',
  IE: '/images/flags/IE.svg',
  IL: '/images/flags/IL.svg',
  IM: '/images/flags/IM.svg',
  IN: '/images/flags/IN.svg',
  IR: '/images/flags/IR.svg',
  IS: '/images/flags/IS.svg',
  IT: '/images/flags/IT.svg',
  JE: '/images/flags/JE.svg',
  JM: '/images/flags/JM.svg',
  JO: '/images/flags/JO.svg',
  JP: '/images/flags/JP.svg',
  KE: '/images/flags/KE.svg',
  KG: '/images/flags/KG.svg',
  KH: '/images/flags/KH.svg',
  KM: '/images/flags/KM.svg',
  KN: '/images/flags/KN.svg',
  KP: '/images/flags/KP.svg',
  KR: '/images/flags/KR.svg',
  KW: '/images/flags/KW.svg',
  KY: '/images/flags/KY.svg',
  KZ: '/images/flags/KZ.svg',
  LA: '/images/flags/LA.svg',
  LB: '/images/flags/LB.svg',
  LC: '/images/flags/LC.svg',
  LI: '/images/flags/LI.svg',
  LK: '/images/flags/LK.svg',
  LR: '/images/flags/LR.svg',
  LS: '/images/flags/LS.svg',
  LT: '/images/flags/LT.svg',
  LU: '/images/flags/LU.svg',
  LV: '/images/flags/LV.svg',
  LY: '/images/flags/LY.svg',
  MA: '/images/flags/MA.svg',
  MC: '/images/flags/MC.svg',
  MD: '/images/flags/MD.svg',
  ME: '/images/flags/ME.svg',
  MG: '/images/flags/MG.svg',
  MK: '/images/flags/MK.svg',
  ML: '/images/flags/ML.svg',
  MM: '/images/flags/MM.svg',
  MN: '/images/flags/MN.svg',
  MO: '/images/flags/MO.svg',
  MR: '/images/flags/MR.svg',
  MS: '/images/flags/MS.svg',
  MT: '/images/flags/MT.svg',
  MU: '/images/flags/MU.svg',
  MV: '/images/flags/MV.svg',
  MW: '/images/flags/MW.svg',
  MX: '/images/flags/MX.svg',
  MY: '/images/flags/MY.svg',
  MZ: '/images/flags/MZ.svg',
  NA: '/images/flags/NA.svg',
  NE: '/images/flags/NE.svg',
  NG: '/images/flags/NG.svg',
  NI: '/images/flags/NI.svg',
  NL: '/images/flags/NL.svg',
  NO: '/images/flags/NO.svg',
  NP: '/images/flags/NP.svg',
  NZ: '/images/flags/NZ.svg',
  OM: '/images/flags/OM.svg',
  PA: '/images/flags/PA.svg',
  PE: '/images/flags/PE.svg',
  PF: '/images/flags/PF.svg',
  PG: '/images/flags/PG.svg',
  PH: '/images/flags/PH.svg',
  PK: '/images/flags/PK.svg',
  PL: '/images/flags/PL.svg',
  PR: '/images/flags/PR.svg',
  PT: '/images/flags/PT.svg',
  PW: '/images/flags/PW.svg',
  PY: '/images/flags/PY.svg',
  QA: '/images/flags/QA.svg',
  RO: '/images/flags/RO.svg',
  RS: '/images/flags/RS.svg',
  RU: '/images/flags/RU.svg',
  RW: '/images/flags/RW.svg',
  SA: '/images/flags/SA.svg',
  SB: '/images/flags/SB.svg',
  SC: '/images/flags/SC.svg',
  SD: '/images/flags/SD.svg',
  SE: '/images/flags/SE.svg',
  SG: '/images/flags/SG.svg',
  SH: '/images/flags/SH.svg',
  SI: '/images/flags/SI.svg',
  SK: '/images/flags/SK.svg',
  SL: '/images/flags/SL.svg',
  SM: '/images/flags/SM.svg',
  SN: '/images/flags/SN.svg',
  SO: '/images/flags/SO.svg',
  SR: '/images/flags/SR.svg',
  ST: '/images/flags/ST.svg',
  SV: '/images/flags/SV.svg',
  SY: '/images/flags/SY.svg',
  SZ: '/images/flags/SZ.svg',
  TC: '/images/flags/TC.svg',
  TD: '/images/flags/TD.svg',
  TG: '/images/flags/TG.svg',
  TH: '/images/flags/TH.svg',
  TJ: '/images/flags/TJ.svg',
  TL: '/images/flags/TL.svg',
  TM: '/images/flags/TM.svg',
  TN: '/images/flags/TN.svg',
  TO: '/images/flags/TO.svg',
  TQ: '/images/flags/TQ.svg',
  TR: '/images/flags/TR.svg',
  TT: '/images/flags/TT.svg',
  TW: '/images/flags/TW.svg',
  TZ: '/images/flags/TZ.svg',
  UA: '/images/flags/UA.svg',
  US: '/images/flags/USA.svg',
  UG: '/images/flags/UG.svg',
  UZ: '/images/flags/UZ.svg',
  VC: '/images/flags/VC.svg',
  VE: '/images/flags/VE.svg',
  VG: '/images/flags/VG.svg',
  VN: '/images/flags/VN.svg',
  VU: '/images/flags/VU.svg',
  WS: '/images/flags/WS.svg',
  WW: '/images/flags/WW.svg',
  YE: '/images/flags/YE.svg',
  YU: '/images/flags/YU.svg',
  ZA: '/images/flags/ZA.svg',
  ZM: '/images/flags/ZM.svg',
  ZW: '/images/flags/ZW.svg',
};
